import { Form, Formik } from "formik"
import { type ChangeEvent, useState } from "react"
import { useTranslation } from "react-i18next"
import { IoChevronBackCircleOutline } from "react-icons/io5"
import { useParams } from "react-router"
import { Link } from "react-router-dom"
import { toast } from "react-toastify"

import VoiceRecorder from "@/components/VoiceRecorder/VoiceRecorder"
import { useReplyToTicketMutation } from "@/queries/mutations/ticketMutations"
import { useSingleTicketData } from "@/queries/queries"
import {
  fileIcon,
  micPermissionIcon,
  pauseIcon,
  sendIcon,
} from "@/utils/variables"

import TicketChatContainer from "./TicketChatContainer"
import style from "./TicketsStyle.module.css"

export default function TicketsChatShow() {
  const [start, setStart] = useState(false)
  const [isRecording, setIsRecording] = useState(false)

  const [permissionError, setPermissionError] = useState(false)

  const { chatId } = useParams()
  const { data } = useSingleTicketData(Number(chatId))

  const replyToTicketMutation = useReplyToTicketMutation(Number(chatId))

  const handleFileUpload = (
    event: ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: unknown) => void
  ) => {
    const file = event.target.files?.[0]
    if (file) {
      const allowedExtensions = ["image/jpeg", "image/jpg", "image/png"]
      if (!allowedExtensions.includes(file.type)) {
        setFieldValue("content_file", "")
        setFieldValue("type", "0")
        toast.error("Invalid file type. Please upload a JPEG or PNG image.")
        return // Exit the function to prevent state update
      }

      setFieldValue("content_file", file)
      setFieldValue("type", "2")
    }
  }

  const toggleVoiceRecorder = () => {
    if (!permissionError) {
      setStart(!start)
      setIsRecording(false)
    } else {
      setPermissionError(true)
    }
  }
  const { t } = useTranslation()
  return (
    <>
      <Link to={"/tickets"} className={style["back_btn"]}>
        <IoChevronBackCircleOutline color="black" /> {t("ticketsPage.title")}
      </Link>
      {data && (
        <div className="card  mb-4 border-0">
          <div className="card-body">
            <p className="card-text mb-3 fs-5">
              <strong>{t("ticketsPage.chatShow.subject")}</strong>{" "}
              {data.subject}
            </p>
            <p className="card-text mb-2 fs-5">
              <strong>{t("ticketsPage.chatShow.description")}</strong>{" "}
              {data.description}
            </p>
          </div>
        </div>
      )}
      <Formik
        initialValues={{
          content_file: null,
          note: "",
          type: "0",
          content: "",
        }}
        onSubmit={(values, { resetForm }) => {
          if (values.content_file || values.content) {
            replyToTicketMutation.mutate(values, {
              onSuccess: () => {
                resetForm()
                setStart(false) // Stop the voice recorder if it was active
                setIsRecording(false)
              },
            })
          }
        }}
      >
        {({ setFieldValue, values, handleChange, handleBlur }) => {
          return (
            <Form
              style={{ position: "relative" }}
              className={style["form-con"]}
            >
              {/* Permission Error Modal */}

              <div
                className={`${style["permission"]} ${permissionError && style["active"]}`}
              >
                <span>{micPermissionIcon}</span>
                <p>{t("ticketsPage.chatShow.micPermission")} </p>
                <button type="button" onClick={() => setPermissionError(false)}>
                  {t("buttons.ok")}
                </button>
              </div>

              <TicketChatContainer
                values={values}
                data={data?.ticket_replies}
              />
              {data?.status != 3 && data?.status != 0 && (
                <div className="d-flex align-items-center gap-3">
                  <div className={`flex-grow-1 ${style["input-con"]}`}>
                    {/* Voice Recorder */}
                    {start ? (
                      <VoiceRecorder
                        setFieldValue={setFieldValue}
                        onPermissionError={() => {
                          setPermissionError(true)
                          setStart(false) // Ensure recorder does not start
                        }}
                        onRecordingStart={() => {
                          setIsRecording(true)
                        }}
                        onRecordingStop={(url: string) =>
                          setFieldValue("content_file", url)
                        }
                        startExternal={start}
                      />
                    ) : (
                      <>
                        {values.type == "1" ? (
                          <audio
                            style={{ height: "34px" }}
                            className="w-100"
                            controls
                          >
                            <source
                              src={
                                values.content_file
                                  ? URL.createObjectURL(values.content_file)
                                  : ""
                              }
                              type="audio/wav"
                            />
                          </audio>
                        ) : values.type == "2" ? (
                          <div className="flex-grow w-100">
                            <img
                              src={
                                values.content_file
                                  ? URL.createObjectURL(values.content_file)
                                  : ""
                              }
                              className="border border-2 "
                              width={"40"}
                              height={"40px"}
                              alt=""
                            />
                          </div>
                        ) : (
                          <input
                            type="text"
                            name={values.type == "0" ? "content" : "note"}
                            placeholder={
                              values.type !== "0"
                                ? t("ticketsPage.chatShow.note.placeholder")
                                : t("ticketsPage.chatShow.content.placeholder")
                            }
                            value={
                              values.type == "0" ? values.content : values.note
                            }
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className=""
                          />
                        )}
                      </>
                    )}
                    {/* File Upload */}
                    {!isRecording && (
                      <>
                        <label
                          htmlFor="file-upload"
                          style={{ cursor: "pointer", display: "none" }}
                        >
                          {fileIcon}
                        </label>
                        <input
                          id="file-upload"
                          type="file"
                          accept="image/*"
                          disabled={replyToTicketMutation.isLoading}
                          style={{ display: "none" }}
                          onChange={(e) => handleFileUpload(e, setFieldValue)}
                        />
                      </>
                    )}
                  </div>
                  {isRecording ? (
                    <button
                      type="button"
                      className={style["pause_btn"]}
                      onClick={() => {
                        toggleVoiceRecorder()
                      }}
                    >
                      <span>{pauseIcon}</span>
                    </button>
                  ) : (
                    <button
                      disabled={replyToTicketMutation.isPending}
                      type="submit"
                      className={style["send_btn"]}
                    >
                      {replyToTicketMutation.isPending ? (
                        <div
                          className="spinner-border spinner-border-sm text-white"
                          role="status"
                        />
                      ) : (
                        <span>{sendIcon}</span>
                      )}
                    </button>
                  )}
                </div>
              )}
            </Form>
          )
        }}
      </Formik>
    </>
  )
}
